/** Renders a page (Impressum, Datenschutz) from its stored content blocks. */

import { BlockRenderer } from '../components/Blocks'
import { Row, Section } from '../components/ui'
import type { PageContent } from '../lib/api'
import { useApi, useDocumentMeta, useHreflang, useLocale } from '../lib/hooks'
import { pagePaths, t, type PageKey } from '../lib/i18n'

export default function ContentPage({ pageKey }: { pageKey: PageKey }) {
  const locale = useLocale()
  const strings = t(locale)
  const { data, loading, error } = useApi<PageContent>(
    `/api/pages/${pageKey}?locale=${locale}`,
  )

  useDocumentMeta(data?.title || 'Zvi Dori Geigenbau')
  useHreflang(pagePaths(pageKey))

  if (loading) {
    return (
      <Section>
        <Row>
          <p className="font-sans text-[18px] text-slate">{strings.loading}</p>
        </Row>
      </Section>
    )
  }

  if (error || !data) {
    return (
      <Section>
        <Row>
          <h1 className="font-serif text-[44px] text-ink">{strings.notFound.title}</h1>
          <p className="font-sans text-[18px] text-ink">{error ?? strings.notFound.body}</p>
        </Row>
      </Section>
    )
  }

  return (
    <>
      <BlockRenderer blocks={data.blocks} />
      {pageKey === 'impressum' ? <OriginalSiteCredit label={strings.credits.originalSite} /> : null}
    </>
  )
}

/** Who made the site this one replaced - a line the owner has promised to
 *  carry. It lives here rather than in the page's stored blocks so that
 *  rewording the Impressum in the admin cannot drop it by accident. */
function OriginalSiteCredit({ label }: { label: string }) {
  return (
    <Section className="!pb-14 !pt-0 md:!pb-14 md:!pt-0">
      <Row>
        <p
          className="m-0 max-w-[820px] font-sans text-[14px] leading-[1.65] text-slate"
          data-testid="original-site-credit"
        >
          {label}:{' '}
          <a
            href="https://vonki.net"
            target="_blank"
            rel="noreferrer noopener"
            className="hover:underline"
          >
            Katharina von Issendorff, VONKI Solutions
          </a>
        </p>
      </Row>
    </Section>
  )
}
